import { MerklRewardLegacy, Percent } from "@silo/types";
import { asApr } from "@silo/utils";
import { getBorrowAprMetrics, getDepositAprMetrics } from "./utils";
import { DepositBaseApr, DepositRewardApr } from "./deposit-apr-breakdown";
import { BorrowTotal } from "./borrow-total";
import { BorrowBaseApr, BorrowRewardApr } from "./borrow-apr-breakdown";
import { KeyValueList } from "../key-value-list";
import { classNames } from "../../utils/class-names";
import { GLOBAL_CONFIG } from "@silo/config";

export interface PositionDepositProps {
  depositApr: bigint;
  depositRewardsApr: bigint;
  isProtected: boolean;
  rewardsSymbol: string;
  merklRewards: MerklRewardLegacy[];
  revenueApr: Percent;
}

export function PositionDeposit({
  depositApr,
  depositRewardsApr,
  isProtected,
  rewardsSymbol,
  merklRewards,
  revenueApr,
}: PositionDepositProps) {
  const metrics = getDepositAprMetrics({
    depositApr,
    depositRewardsApr,
    isProtected,
    rewardsSymbol,
    merklRewards,
  });

  const { depositRewards } = metrics;

  const hasProtectedRevenue = isProtected && revenueApr > 0n;

  const hasDepositApr = metrics.hasDepositApr || hasProtectedRevenue;
  const totalDepositApr = metrics.totalDepositApr + revenueApr;

  const hasDepositIncentives =
    metrics.hasDepositRewardsApr || hasProtectedRevenue;

  return (
    <div className="flex flex-col">
      <KeyValueList.Pair>
        <KeyValueList.Key>APR</KeyValueList.Key>

        <KeyValueList.Value
          className={classNames(
            "font-mono font-medium",
            hasDepositIncentives ? "text-amber-300" : "text-white"
          )}
          dataCy="apy"
        >
          {hasDepositApr ? asApr(totalDepositApr) : "--"}
        </KeyValueList.Value>
      </KeyValueList.Pair>

      {hasDepositIncentives && (
        <div className="flex flex-col items-end text-right">
          <DepositBaseApr
            className="text-sm"
            depositApr={depositApr}
            isProtected={isProtected}
          />

          {hasProtectedRevenue && (
            <div className="flex items-center gap-2.5 text-shale-200 text-sm">
              <span className="font-mono font-medium text-white">
                {asApr(revenueApr)}
              </span>

              <span>
                revenue APR in {GLOBAL_CONFIG.revenue.rewardTokenSymbol}
              </span>
            </div>
          )}

          {depositRewards.map((reward, i) => (
            <DepositRewardApr className="text-sm" key={i} reward={reward} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface PositionBorrowProps {
  borrowApr: bigint;
  borrowRewardsApr: bigint;
  rewardsSymbol: string;
  merklRewards: MerklRewardLegacy[];
  isBorrowAprFixed: boolean;
}

export function PositionBorrow({
  borrowApr,
  borrowRewardsApr,
  rewardsSymbol,
  merklRewards,
  isBorrowAprFixed,
}: PositionBorrowProps) {
  const { hasBorrowRewardsApr, totalBorrowApr, borrowRewards } =
    getBorrowAprMetrics({
      borrowApr,
      borrowRewardsApr,
      rewardsSymbol,
      merklRewards,
    });

  return (
    <div className="flex flex-col">
      <KeyValueList.Pair>
        <KeyValueList.Key>APR</KeyValueList.Key>

        <BorrowTotal.Root
          hasBorrowRewards={hasBorrowRewardsApr}
          totalBorrowApr={totalBorrowApr}
          isBorrowAprFixed={isBorrowAprFixed}
          className="items-end"
        >
          <BorrowTotal.Value
            className="text-base"
            totalBorrowApr={totalBorrowApr}
            dataCy="apy"
          />

          {isBorrowAprFixed && <BorrowTotal.Fixed className="text-sm" />}
        </BorrowTotal.Root>
      </KeyValueList.Pair>

      {hasBorrowRewardsApr && (
        <div className="flex flex-col items-end text-right">
          <BorrowBaseApr className="text-sm" borrowApr={borrowApr} />

          {borrowRewards.map((reward, i) => (
            <BorrowRewardApr className="text-sm" key={i} reward={reward} />
          ))}
        </div>
      )}
    </div>
  );
}
